import React from 'react';
import ModelsAPI from '../../api/models';
import { AIModel, formatPrice } from '../../api/structs/model';
import type { CatalogStatus, ModelOption } from './ModelMultiSelect';

export const loadImageModels = () => ModelsAPI.getImageModels();
export const loadChatModels = () => ModelsAPI.getChatModels();

/** The registry's list, and whether it has answered. A list that cannot be read is empty and says so. */
export const useCatalog = (load: () => Promise<AIModel[]>): { models: AIModel[]; status: CatalogStatus } => {
  const [state, setState] = React.useState<{ models: AIModel[]; status: CatalogStatus }>({ models: [], status: 'loading' });
  React.useEffect(() => {
    let cancelled = false;
    load()
      .then((models) => !cancelled && setState({ models: models ?? [], status: 'ready' }))
      .catch(() => !cancelled && setState({ models: [], status: 'failed' }));
    return () => {
      cancelled = true;
    };
  }, [load]);
  return state;
};

/** The price per picture, only when the catalog carries one. */
export const imageOption = (model: AIModel): ModelOption => ({
  provider: model.provider,
  model: model.id,
  name: model.name || model.id,
  ...(model.pricing?.per_image ? { price: formatPrice(model) } : {}),
});

/** Writing is done by chat models that can use tools. */
export const chatModelsForWriting = (models: AIModel[]): ModelOption[] =>
  models
    .filter((model) => model.capabilities?.features?.includes('tools'))
    .map((model) => ({ provider: model.provider, model: model.id, name: model.name || model.id }));
